import React, { useMemo } from 'react';
import classNames from 'classnames';
import Avatar from 'components/base/Avatar';
import Lightbox from 'components/base/LightBox';
import { Message as MessageType, User, actions } from 'data/chat';
import useLightbox from 'hooks/useLightbox';
import MessageActionButtons from './MessageActionButtons';
import MessageAttachments from './MessageAttachments';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckDouble } from '@fortawesome/free-solid-svg-icons';
import AttachmentPreview from 'components/common/AttachmentPreview';

// Definimos una interfaz para los adjuntos de imagen para evitar el 'any'
interface ImageAttachment {
  src: string;
}

interface MessageProps {
  message: MessageType;
  user: User;
  showActions?: boolean;
}

const Message = ({ message, user, showActions = true }: MessageProps) => {
  // 1. Unificamos la propiedad
  const textoContenido = message.message || (message as { body?: string }).body || '';
  // 2. Filtros estrictos

// 2. Filtros estrictos para detectar multimedia (Soporta URLs absolutas y relativas)
  const isAudio = textoContenido.includes('uploads/whatsapp/audio/') || textoContenido.includes('uploads/whatsapp/audios/');
  const isImage = textoContenido.includes('uploads/whatsapp/imagenes/');
  const isDocument = textoContenido.includes('uploads/whatsapp/documentos/');  
  // 3. FUNCIÓN INTELIGENTE RESOLVEDORA DE RUTAS MULTIMEDIA
  const getMediaUrl = (path: string) => {
    if (!path) return '';
    
    // Si la BD guardó la URL completa (ej: archivos enviados desde el frontend vía multer-s3)
    if (path.startsWith('http')) return path;

    // Detectamos si estamos en producción chequeando tu variable de entorno
    const envApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000';
    const isProduction = envApiUrl.includes('estudioparkes.com');
    
    if (isProduction) {
      // Redirigimos al Bucket S3 de AWS (Asegúrate de que la región sea la correcta, usualmente us-east-1)
      return `https://procureitor-files-2025.s3.us-east-1.amazonaws.com/${path}`;
    } else {
      // En entorno local seguimos usando localhost
      const baseUrl = envApiUrl.endsWith('/') ? envApiUrl : `${envApiUrl}/`;
      return `${baseUrl}${path}`;
    }
  };

  // Obtenemos la URL final y absoluta para la multimedia
  const fullMediaUrl = getMediaUrl(textoContenido);

  // 4. Limpiamos el Lightbox usando la función inteligente
  const imagesForLightbox = useMemo(() => {
    const imgs: string[] = []; 
    
    if (isImage && textoContenido) {
      imgs.push(fullMediaUrl); 
    }
    
    if (message.attachments?.images && Array.isArray(message.attachments.images)) {
        message.attachments.images.forEach((img: string | ImageAttachment) => {
          const url = typeof img === 'string' ? img : img?.src;
        if (url) imgs.push(getMediaUrl(url));
      });
    }
    
    return imgs;
  }, [isImage, textoContenido, message.attachments?.images, fullMediaUrl]);

  const { lightboxProps, openLightbox } = useLightbox(imagesForLightbox);

  return (
    <div className="d-flex chat-message">
      <div
        className={classNames('d-flex flex-1', {
          'justify-content-end': message.type === 'sent'
        })}
      >
        <div
          className={classNames('w-100', {
            'w-xxl-75': showActions
          })}
        >
          <div
            className={classNames('d-flex hover-actions-trigger', {
              'flex-end-center': message.type === 'sent'
            })}
          >
            {message.type === 'received' && (
              <Avatar
                src={user.avatar}
                size="m"
                className="me-3 flex-shrink-0"
              />
            )}

            {message.type === 'sent' && showActions && (
              <MessageActionButtons actions={actions} variant="sent" />
            )}

              <div
              className={classNames('chat-message-content me-2', {
                received: message.type === 'received'
              })}
              >
              <div
                className={classNames('mb-1', {
                  'sent-message-content ': message.type === 'sent',
                  'received-message-content border border-translucent':
                    message.type === 'received',
                  attachments:
                    Number(message.attachments?.images?.length) > 0 &&
                    !textoContenido
                })}
              >

                  {textoContenido && (
                  <>
                    {isAudio ? (
                      <div className="p-2" style={{ minWidth: '220px' }}>
                        <audio controls className="w-100 mt-1">
                          {/* Usa fullMediaUrl en lugar de la concatenación anterior */}
                          <source src={fullMediaUrl} type="audio/ogg" />
                        </audio>
                      </div>
                    ) : isImage ? (
                      <div className="p-1">
                        <img 
                          src={fullMediaUrl} 
                          alt="WhatsApp" 
                          className="img-fluid rounded-2 cursor-pointer"
                          style={{ maxHeight: '300px', cursor: 'pointer', objectFit: 'cover' }}
                          onClick={() => openLightbox(1)} 
                        />
                      </div>
                    ) : isDocument ? (
                      <div className="p-2" style={{ minWidth: '250px' }}>
                        <div 
                          onClick={() => window.open(fullMediaUrl, '_blank', 'noopener,noreferrer')}
                          style={{ cursor: 'pointer' }}
                        >
                          <AttachmentPreview
                            variant={message.type === 'received' ? 'primary' : 'secondary'}
                            url="#!" 
                            attachment={{
                              name: textoContenido.split('/').pop() || 'Documento', 
                              size: 'Abrir en pestaña nueva',
                              format: textoContenido.split('.').pop()?.toUpperCase() || 'PDF'
                            }} 
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="mb-0">{textoContenido}</p>
                    )}
                  </>
                )}

                {message.attachments?.images && (
                  <MessageAttachments
                    attachments={message.attachments.images}
                    openLightbox={openLightbox}
                  />
                )}
                {message.attachments?.file && (
                  <AttachmentPreview
                    attachment={message.attachments.file}
                    variant={
                      message.type === 'received' ? 'primary' : 'secondary'
                    }
                  />
                )}
              </div>
            </div>

            {message.type === 'received' && showActions && (
              <MessageActionButtons
                actions={actions.slice(1)}
                variant="received"
              />
            )}
          </div>


<div
            className={classNames('d-flex gap-1 fs-10 align-items-center', {
              'ms-7': message.type === 'received',
              'justify-content-end': message.type === 'sent'
            })}
          >
            {/* 👇 NUEVO: Mostrar iniciales de quién lo envió 👇 */}
            {message.type === 'sent' && message.miem && (
              <span className="fw-bold text-primary me-1">
                Enviado Por: {message.miem} •
              </span>
            )}
            
            <p className="mb-0 text-body-tertiary text-opacity-85 fw-semibold">
              {message.time}
            </p>
            
            {message.readAt && (
              <FontAwesomeIcon icon={faCheckDouble} className="text-success ms-1" />
            )}
          </div>


          {imagesForLightbox.length > 0 && <Lightbox {...lightboxProps} />}
        </div>
      </div>
    </div>
  );
};

export default Message;